import React from 'react';
import { MessageCircleIcon } from 'lucide-react';
import { WHATSAPP_GABRIELA, WHATSAPP_CHANIRA } from '../config';
import { QUEENS_PHOTO, QUEEN_FOCUS } from '../data/photos';
import { useLanguage } from '../contexts/LanguageContext';
import { useLead } from '../contexts/LeadContext';
import { PhotoPlaceholder } from './PhotoPlaceholder';
import { trackWhatsAppContact } from '../tracking';
import type { QueenName } from '../types/lead';
interface Queen {
  name: QueenName;
  number: string;
  photoKey: string;
  altKey: string;
}
const QUEENS: Queen[] = [{
  name: 'Gabriela',
  number: WHATSAPP_GABRIELA,
  photoKey: 'queens.photo.gabriela',
  altKey: 'queens.alt.gabriela'
}, {
  name: 'Chanira',
  number: WHATSAPP_CHANIRA,
  photoKey: 'queens.photo.chanira',
  altKey: 'queens.alt.chanira'
}];
interface QueenPickerProps {
  /** Where the picker was opened from — tracked alongside the Queen name. */
  context: string;
  tone?: 'dark' | 'light';
  onSelected?: () => void;
}

/** Reusable Queen selection used by every WhatsApp entry point. */
export function QueenPicker({
  context,
  tone = 'light',
  onSelected
}: QueenPickerProps) {
  const {
    t,
    lang
  } = useLanguage();
  const {
    leadName,
    vehicleInterest,
    hasSubmitted
  } = useLead();
  const buildMessage = (): string => {
    if (!hasSubmitted || !leadName) return t('picker.msg.generic');
    const vehicleLabel = vehicleInterest ? t(`form.opt.${vehicleInterest}`).toLowerCase() : t('form.opt.not-sure').toLowerCase();
    return t('picker.msg.named', {
      name: leadName,
      vehicle: vehicleLabel
    });
  };
  const handleSelect = (queen: Queen) => {
    if (!queen.number) return;
    trackWhatsAppContact(queen.name, context);
    const url = `https://wa.me/${queen.number}?text=${encodeURIComponent(buildMessage())}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    onSelected?.();
  };
  const isDark = tone === 'dark';
  return <div data-id="element-224" data-mp="24cg9p9ppnvrz">
      <p className={`font-display text-lg font-bold ${isDark ? 'text-white' : 'text-ink'}`} id={`queen-picker-title-${context}`} data-id="element-225" data-mp="24d86gnv1nzju">
        {t('picker.title')}
      </p>
      <p className={`mt-1 text-sm ${isDark ? 'text-white/60' : 'text-muted'}`} data-id="element-226" data-mp="5v60c5121is0">{t('picker.sub')}</p>

      <ul className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-227" data-mp="11toywldz8h7">
        {QUEENS.map(queen => {
        const available = Boolean(queen.number);
        return <li key={queen.name} className={`overflow-hidden border ${isDark ? 'border-white/15 bg-white/[0.03]' : 'border-camel/40 bg-white'}`} data-id="element-228" data-mp="3nal8njyejgcd">
              {/* Both cards crop the same shared photo to their own Queen. */}
              <div className="aspect-[4/5] w-full overflow-hidden bg-offwhite" data-id="element-229" data-mp="39bd6qjautu4h">
                {QUEENS_PHOTO ? <img src={QUEENS_PHOTO} alt={t(queen.altKey)} loading="lazy" decoding="async" style={{
              objectPosition: QUEEN_FOCUS[queen.name]
            }} className="h-full w-full scale-[1.35] object-cover" data-id="element-230" data-mp="1cgyv6akq7p5k" /> : <PhotoPlaceholder label={t(queen.photoKey)} tone={isDark ? 'dark' : 'light'} data-id="element-231" data-mp="1osswezskvqvi" />}
              </div>
              <div className="p-4" data-id="element-232" data-mp="1tqkn5qhhl6y4">
                <p className={`font-display text-base font-extrabold uppercase tracking-[0.14em] ${isDark ? 'text-white' : 'text-ink'}`} data-id="element-233" data-mp="1ttowt3dq0uw6">
                  {queen.name}
                </p>
                {/* WhatsApp green + icon appear ONLY on controls that actually open WhatsApp. */}
                <button type="button" onClick={() => handleSelect(queen)} disabled={!available} aria-disabled={!available}
            // Disabled because the matching WhatsApp constant in config.ts is empty.
            // Never fall back to a placeholder number.
            className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 bg-whatsapp px-4 text-sm font-bold uppercase tracking-[0.12em] text-golddeep transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-muted/25 disabled:text-muted" data-id="element-234" data-mp="1b25znd6dob22">
                  <MessageCircleIcon aria-hidden="true" className="h-4 w-4" data-id="element-235" data-mp="2p32a753buvcr" />
                  {available ? `${t('picker.button')} · ${queen.name}` : t('picker.unavailable')}
                </button>
                {!available && <p className={`mt-2 text-xs ${isDark ? 'text-white/45' : 'text-muted'}`} data-id="element-236" data-mp="3cqjvqiw00x7a">
                    {lang === 'es' ? 'Falta configurar el número en config.ts.' : 'Number pending in config.ts.'}
                  </p>}
              </div>
            </li>;
      })}
      </ul>
    </div>;
}